import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useColorModeValue,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select,
  useDisclosure,
  useToast,
  Text,
  HStack,
  Badge,
  Tooltip,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import { useCustomFields } from '../../../contexts/CustomFieldsContext';
import { CustomField, FieldType, FieldCategory, VisualizationType, FrequencySettings } from '../../../types/customFields';
import { useEncryption } from '../../../contexts/EncryptionContext';

interface CustomFieldsEditorProps {
  journalEntryId?: string;
  onFieldChange: (fieldId: string, value: any) => void;
}

const CustomFieldsEditor: React.FC<CustomFieldsEditorProps> = ({
  journalEntryId,
  onFieldChange,
}) => {
  const { customFields, getFieldEntriesForJournal, createCustomField } = useCustomFields();
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { decrypt } = useEncryption();

  const [newField, setNewField] = useState<Partial<CustomField>>({
    name: '',
    field_type: 'number',
    category: 'habit',
    visualization_type: 'calendar',
    show_in_new_entries: true,
    is_archived: false,
    target_value: { min: 0, max: 100 },
    frequency: {
      type: 'daily',
      times_per_period: 1,
    },
    reminder_settings: {
      enabled: false,
      times: ['09:00'],
      notification_type: 'push',
    },
  });

  // Common units for different measurements
  const commonUnits = {
    count: ['times', 'units', 'servings'],
    duration: ['minutes', 'hours'],
    distance: ['steps', 'km', 'miles'],
    volume: ['ml', 'L', 'oz', 'cups'],
    weight: ['kg', 'lbs', 'g'],
  };

  useEffect(() => {
    loadFieldValues();
  }, [journalEntryId, customFields]);

  const loadFieldValues = async () => {
    if (journalEntryId) {
      const entries = await getFieldEntriesForJournal(journalEntryId);
      const values: Record<string, any> = {};
      for (const entry of entries) {
        try {
          values[entry.custom_field_id] = await decrypt(entry.encrypted_value);
        } catch (error) {
          console.error('Failed to decrypt field value:', error);
          values[entry.custom_field_id] = '';
        }
      }
      setFieldValues(values);
    } else {
      const values: Record<string, any> = {};
      customFields
        .filter((field) => field.show_in_new_entries && !field.is_archived)
        .forEach((field) => {
          values[field.id] = getDefaultValue(field);
        });
      setFieldValues(values);
    }
  };

  const getDefaultValue = (field: CustomField) => {
    switch (field.field_type) {
      case 'boolean':
        return false;
      case 'scale':
        return field.target_value?.min || 0;
      case 'time_duration':
        return 0;
      case 'number':
        return field.target_value?.min || 0;
      default:
        return '';
    }
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    onFieldChange(fieldId, value);
  };

  const handleCreateField = async () => {
    try {
      await createCustomField(newField as CustomField);
      onClose();
      toast({
        title: 'Custom field created',
        status: 'success',
        duration: 3000,
      });
      resetNewField();
    } catch (error) {
      toast({
        title: 'Error creating custom field',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const resetNewField = () => {
    setNewField({
      name: '',
      field_type: 'number',
      category: 'habit',
      visualization_type: 'calendar',
      show_in_new_entries: true,
      is_archived: false,
      target_value: { min: 0, max: 100 },
      frequency: {
        type: 'daily',
        times_per_period: 1,
      },
      reminder_settings: {
        enabled: false,
        times: ['09:00'],
        notification_type: 'push',
      },
    });
  };

  const renderField = (field: CustomField) => {
    const value = fieldValues[field.id] ?? getDefaultValue(field);

    switch (field.field_type) {
      case 'text':
        return (
          <Input
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={`Enter ${field.name.toLowerCase()}`}
          />
        );

      case 'number':
      case 'scale':
        return (
          <HStack>
            <NumberInput
              value={value}
              min={field.target_value?.min}
              max={field.target_value?.max}
              step={1}
              onChange={(_, val) => handleFieldChange(field.id, val)}
              flex="1"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            {field.unit && (
              <Text color="gray.500" fontSize="sm">
                {field.unit}
              </Text>
            )}
          </HStack>
        );

      case 'time_duration':
        return (
          <HStack>
            <NumberInput
              value={value}
              min={0}
              step={1}
              onChange={(_, val) => handleFieldChange(field.id, val)}
              flex="1"
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Text color="gray.500" fontSize="sm">
              minutes
            </Text>
          </HStack>
        );

      case 'boolean':
        return (
          <Switch
            isChecked={value}
            onChange={(e) => handleFieldChange(field.id, e.target.checked)}
          />
        );

      default:
        return null;
    }
  };

  const renderFieldGroup = (category: FieldCategory) => {
    const fields = customFields.filter(
      (field) =>
        field.category === category &&
        !field.is_archived &&
        (journalEntryId || field.show_in_new_entries)
    );

    if (fields.length === 0) return null;

    return (
      <Box mb={4}>
        <Text fontSize="md" fontWeight="medium" mb={2}>
          {category.charAt(0).toUpperCase() + category.slice(1)}s
        </Text>
        <VStack spacing={4} align="stretch">
          {fields.map((field) => (
            <FormControl key={field.id}>
              <FormLabel>
                <HStack>
                  <Text>{field.name}</Text>
                  {field.target_value?.target && (
                    <Tooltip label={`Target: ${field.target_value.target} ${field.unit || ''}`}>
                      <Badge colorScheme="green">Target</Badge>
                    </Tooltip>
                  )}
                </HStack>
              </FormLabel>
              {renderField(field)}
            </FormControl>
          ))}
        </VStack>
      </Box>
    );
  };

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Text fontSize="md" fontWeight="medium">Tracking & Habits</Text>
        <Button
          leftIcon={<FiPlus />}
          size="sm"
          colorScheme="blue"
          variant="ghost"
          onClick={onOpen}
        >
          Add Field
        </Button>
      </HStack>

      {customFields.length === 0 ? (
        <Text fontSize="sm" color="gray.500" mb={4}>
          No tracking fields yet. Click "Add Field" to create one.
        </Text>
      ) : (
        <Box
          p={4}
          bg={bgColor}
          borderRadius="md"
          border="1px"
          borderColor={borderColor}
        >
          {renderFieldGroup('habit')}
          {renderFieldGroup('metric')}
          {renderFieldGroup('medication')}
          {renderFieldGroup('note')}
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Tracking Field</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Field Name</FormLabel>
                <Input
                  value={newField.name}
                  onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                  placeholder="Enter field name"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select
                  value={newField.category}
                  onChange={(e) => setNewField({ ...newField, category: e.target.value as FieldCategory })}
                >
                  <option value="habit">Habit</option>
                  <option value="metric">Metric</option>
                  <option value="medication">Medication</option>
                  <option value="note">Note</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Field Type</FormLabel>
                <Select
                  value={newField.field_type}
                  onChange={(e) => setNewField({ ...newField, field_type: e.target.value as FieldType })}
                >
                  <option value="number">Number</option>
                  <option value="text">Text</option>
                  <option value="boolean">Yes/No</option>
                  <option value="scale">Scale</option>
                  <option value="time_duration">Time Duration</option>
                </Select>
              </FormControl>

              {(newField.field_type === 'number' || newField.field_type === 'scale') && (
                <FormControl>
                  <FormLabel>Unit</FormLabel>
                  <Select
                    value={newField.unit}
                    onChange={(e) => setNewField({ ...newField, unit: e.target.value })}
                  >
                    <option value="">Select unit</option>
                    {Object.entries(commonUnits).map(([category, units]) => (
                      <optgroup key={category} label={category.charAt(0).toUpperCase() + category.slice(1)}>
                        {units.map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                </FormControl>
              )}

              {newField.category !== 'note' && (
                <>
                  <FormControl>
                    <FormLabel>Target Value</FormLabel>
                    <HStack>
                      <NumberInput
                        value={newField.target_value?.target}
                        onChange={(_, val) =>
                          setNewField({
                            ...newField,
                            target_value: { ...newField.target_value, target: val },
                          })
                        }
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      {newField.unit && <Text>{newField.unit}</Text>}
                    </HStack>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Frequency</FormLabel>
                    <Select
                      value={newField.frequency?.type}
                      onChange={(e) =>
                        setNewField({
                          ...newField,
                          frequency: { ...newField.frequency, type: e.target.value as FrequencySettings['type'] },
                        })
                      }
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="custom">Custom</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Visualization Type</FormLabel>
                    <Select
                      value={newField.visualization_type}
                      onChange={(e) =>
                        setNewField({
                          ...newField,
                          visualization_type: e.target.value as VisualizationType,
                        })
                      }
                    >
                      <option value="calendar">Calendar</option>
                      <option value="line">Line Chart</option>
                      <option value="bar">Bar Chart</option>
                      <option value="pie">Pie Chart</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Reminders</FormLabel>
                    <Switch
                      isChecked={newField.reminder_settings?.enabled}
                      onChange={(e) =>
                        setNewField({
                          ...newField,
                          reminder_settings: {
                            ...newField.reminder_settings,
                            enabled: e.target.checked,
                            times: newField.reminder_settings?.times || ['09:00'],
                            notification_type: newField.reminder_settings?.notification_type || 'push',
                          },
                        })
                      }
                    />
                  </FormControl>
                </>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateField}
              isDisabled={!newField.name || !newField.field_type || !newField.category}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CustomFieldsEditor; 